import type { EndpointSpec } from '@scatterin/api-types';
import type {
  ObservabilityBulkFailureTransitionRequest,
  ObservabilityBulkFailureTransitionResponse,
  ObservabilityCatalogRequest,
  ObservabilityCatalogResponse,
  ObservabilityCompareRequest,
  ObservabilityCompareResponse,
  ObservabilityEnvironmentHealthRequest,
  ObservabilityEnvironmentHealthResponse,
  ObservabilityFailureAcknowledgeRequest,
  ObservabilityFailureAcknowledgeResponse,
  ObservabilityFailureGroupsRequest,
  ObservabilityFailureGroupsResponse,
  ObservabilityFailureQueueRequest,
  ObservabilityFailureQueueResponse,
  ObservabilityFailureTransitionRequest,
  ObservabilityFailureTransitionResponse,
  ObservabilityLogLookupRequest,
  ObservabilityLogLookupResponse,
  ObservabilityQuerySummaryRequest,
  ObservabilityQuerySummaryResponse,
  ObservabilityRunDetailRequest,
  ObservabilityRunDetailResponse,
  ObservabilityRunSearchRequest,
  ObservabilityRunSearchResponse,
  ObservabilityServiceDetailRequest,
  ObservabilityServiceDetailResponse,
  ObservabilityServiceInventoryRequest,
  ObservabilityServiceInventoryResponse,
  ObservabilitySessionDetailRequest,
  ObservabilitySessionDetailResponse,
  ObservabilitySessionSearchRequest,
  ObservabilitySessionSearchResponse,
  ObservabilityUsageInventoryRequest,
  ObservabilityUsageInventoryResponse,
  ObservabilityUsageSubjectRequest,
  ObservabilityUsageSubjectResponse,
  ObservabilityUserAccessListRequest,
  ObservabilityUserAccessListResponse,
  ObservabilityUserAccessUpdateRequest,
  ObservabilityUserAccessUpdateResponse,
} from '@scatterin/api-types/generated/endpoints/analytics';
import { endpoints } from '@scatterin/api-types/generated/endpoints/analytics';

import { signAnalyticsRequest } from '@/lib/authService';

export type { ObservabilityLogLine } from '@scatterin/api-types';
export type { ObservabilityLogLookupRequest, ObservabilityLogLookupResponse } from '@scatterin/api-types/generated/endpoints/analytics';

const defaultBaseUrl = '/';

type JsonRecord = Record<string, unknown>;

export type AnalyticsRequest = {
  body: string;
  credentials: RequestCredentials;
  headers: Record<string, string>;
  method: string;
  url: string;
};

type AnalyticsRequestAuthorizer = (request: AnalyticsRequest) => Promise<AnalyticsRequest> | AnalyticsRequest;

const endpointUrl = (path: string): string => {
  const baseUrl = import.meta.env.VITE_ANALYTICS_API_BASE_URL ?? defaultBaseUrl;
  const normalizedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const normalizedPath = path.replace(/^\/+/, '');
  return normalizedBase ? `${normalizedBase}/${normalizedPath}` : `/${normalizedPath}`;
};

const isJsonRecord = (value: unknown): value is JsonRecord => typeof value === 'object' && value !== null && !Array.isArray(value);

const responseMessage = (value: unknown): string => {
  if (isJsonRecord(value) && typeof value.message === 'string') {
    return value.message;
  }

  if (isJsonRecord(value) && typeof value.error === 'string') {
    return value.error;
  }

  return 'Request failed';
};

const authorizeAnalyticsRequest: AnalyticsRequestAuthorizer = (request) => {
  return signAnalyticsRequest(request);
};

const postJson = async <TRequest, TResponse>(endpoint: EndpointSpec<TRequest, TResponse>, request: TRequest): Promise<TResponse> => {
  const authorizedRequest = await authorizeAnalyticsRequest({
    body: JSON.stringify(request),
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    method: endpoint.method,
    url: endpointUrl(endpoint.path),
  });

  const response = await fetch(authorizedRequest.url, {
    body: authorizedRequest.body,
    credentials: authorizedRequest.credentials,
    headers: authorizedRequest.headers,
    method: authorizedRequest.method,
  });

  let body: unknown;

  try {
    body = await response.json();
  } catch {
    body = {};
  }

  if (!response.ok) {
    throw new Error(responseMessage(body));
  }

  return body as TResponse;
};

export const analyticsApi = {
  acknowledgeFailure: (request: ObservabilityFailureAcknowledgeRequest): Promise<ObservabilityFailureAcknowledgeResponse> =>
    postJson(endpoints.observabilityFailureAcknowledge, request),
  bulkTransitionFailures: (request: ObservabilityBulkFailureTransitionRequest): Promise<ObservabilityBulkFailureTransitionResponse> =>
    postJson(endpoints.observabilityBulkFailureTransition, request),
  catalog: (request: ObservabilityCatalogRequest): Promise<ObservabilityCatalogResponse> =>
    postJson(endpoints.observabilityCatalog, request),
  compare: (request: ObservabilityCompareRequest): Promise<ObservabilityCompareResponse> =>
    postJson(endpoints.observabilityCompare, request),
  environmentHealth: (request: ObservabilityEnvironmentHealthRequest): Promise<ObservabilityEnvironmentHealthResponse> =>
    postJson(endpoints.observabilityEnvironmentHealth, request),
  failureGroups: (request: ObservabilityFailureGroupsRequest): Promise<ObservabilityFailureGroupsResponse> =>
    postJson(endpoints.observabilityFailureGroups, request),
  failureQueue: (request: ObservabilityFailureQueueRequest): Promise<ObservabilityFailureQueueResponse> =>
    postJson(endpoints.observabilityFailureQueue, request),
  failureTransition: (request: ObservabilityFailureTransitionRequest): Promise<ObservabilityFailureTransitionResponse> =>
    postJson(endpoints.observabilityFailureTransition, request),
  logs: (request: ObservabilityLogLookupRequest): Promise<ObservabilityLogLookupResponse> =>
    postJson(endpoints.observabilityLogLookup, request),
  runDetail: (request: ObservabilityRunDetailRequest): Promise<ObservabilityRunDetailResponse> =>
    postJson(endpoints.observabilityRunDetail, request),
  runSearch: (request: ObservabilityRunSearchRequest): Promise<ObservabilityRunSearchResponse> =>
    postJson(endpoints.observabilityRunSearch, request),
  serviceDetail: (request: ObservabilityServiceDetailRequest): Promise<ObservabilityServiceDetailResponse> =>
    postJson(endpoints.observabilityServiceDetail, request),
  serviceInventory: (request: ObservabilityServiceInventoryRequest): Promise<ObservabilityServiceInventoryResponse> =>
    postJson(endpoints.observabilityServiceInventory, request),
  sessionDetail: (request: ObservabilitySessionDetailRequest): Promise<ObservabilitySessionDetailResponse> =>
    postJson(endpoints.observabilitySessionDetail, request),
  sessionSearch: (request: ObservabilitySessionSearchRequest): Promise<ObservabilitySessionSearchResponse> =>
    postJson(endpoints.observabilitySessionSearch, request),
  summary: (request: ObservabilityQuerySummaryRequest): Promise<ObservabilityQuerySummaryResponse> =>
    postJson(endpoints.observabilityQuerySummary, request),
  usageInventory: (request: ObservabilityUsageInventoryRequest): Promise<ObservabilityUsageInventoryResponse> =>
    postJson(endpoints.observabilityUsageInventory, request),
  usageSubject: (request: ObservabilityUsageSubjectRequest): Promise<ObservabilityUsageSubjectResponse> =>
    postJson(endpoints.observabilityUsageSubject, request),
  userAccessList: (request: ObservabilityUserAccessListRequest): Promise<ObservabilityUserAccessListResponse> =>
    postJson(endpoints.observabilityUserAccessList, request),
  userAccessUpdate: (request: ObservabilityUserAccessUpdateRequest): Promise<ObservabilityUserAccessUpdateResponse> =>
    postJson(endpoints.observabilityUserAccessUpdate, request),
};
